import type {
  TrayPredictionItemDto,
  TrayPredictionItemStatus,
  TrayPredictionMaskPoint,
  TrayPredictionResponseDto,
} from '@peritasai/types';

/**
 * One predicted slot, normalized for the app: `mask` is always an array (the
 * service may report `null` for an unlocalizable slot, which the UI treats as `[]`).
 */
export interface TrayPredictionItem {
  name: string;
  classId?: string;
  status: TrayPredictionItemStatus;
  confidence: number;
  mask: TrayPredictionMaskPoint[];
}

/**
 * Domain entity wrapping a `POST /predict` response. Normalizes slot masks and
 * owns the present-count aggregation the scan flow needs to reconcile detected
 * quantities against the count sheet.
 */
export class TrayPredictionEntity {
  /** Identified tray, or null when no tray was recognized. */
  readonly tray: string | null;
  readonly items: TrayPredictionItem[];

  constructor(dto: TrayPredictionResponseDto) {
    this.tray = dto.tray;
    this.items = dto.items.map(normalizeItem);
  }

  /** Whether the service recognized a tray for the scanned frame. */
  get isTrayRecognized(): boolean {
    return this.tray !== null;
  }

  /**
   * Count of `present` slots keyed by slot name. The service reports one item per
   * physical slot, so an instrument's detected quantity is its count of present
   * slots.
   */
  presentCountByName(): Map<string, number> {
    const counts = new Map<string, number>();
    for (const item of this.items) {
      if (item.status === 'present') {
        counts.set(item.name, (counts.get(item.name) ?? 0) + 1);
      }
    }
    return counts;
  }
}

function normalizeItem(item: TrayPredictionItemDto): TrayPredictionItem {
  return {
    name: item.name,
    classId: item.classId,
    status: item.status,
    confidence: item.confidence,
    mask: item.mask ?? [],
  };
}
